import { InterviewersFilter } from "@rooster/types";
import { TFunction } from "i18next";
import moment from "moment-timezone";
import {
  getAvailabilityAdvancedData,
  getAvailabilityData,
  recordSession,
  submitAvailabilityData
} from "../../services/api/availability.api";
import { logErrorAndNotifyUserForRoosterLinks } from "../../services/api/errorLoggerAndNotifier";
import {
  fillRateSchemeType,
  IOverriddenInterviewerPreferences,
  ISubmitAvailabilityPayload
} from "../../services/api/types/availability.type";
import { Provider } from "../../services/api/types/roosterlinks.type";
import {
  HostType,
  InterviewerType,
  MeetingLinkType,
  SchedulerStoreType,
  SessionDataType,
  useSchedulerStore
} from "../../state/scheduler.store";
import { filterPreviouslySelectedSlotFromComputedAvailability } from "../../utils/availability";
import {
  handleRoosterLinkValidationErrorMessageState,
  notifySenderOnNoAvailability,
  setNoAvailabilityError,
  setUnableToLoadAvailabilityForParticipantError
} from "../../utils/errorManagement";
import { logRocketSessionUrl } from "../../utils/logRocket";

const getRequestSessionTiming = (sessionStartedAt: number) => {
  const slotsLoadedAt = new Date().toISOString();

  return {
    slotsLoadedAt,
    pageLoadTimeMs: Date.now() - sessionStartedAt,
    pageLoadedAt: new Date(sessionStartedAt).toISOString()
  };
};

const buildRecordedSlots = (
  computedAvailability: NonNullable<SessionDataType["computedAvailability"]>,
  sessionData: SessionDataType,
  duration: number
) =>
  computedAvailability.flatMap((d) =>
    d.availability
      .filter((s) => s.available)
      .map((s) => {
        const start = moment
          .tz(d.date, sessionData.selectedTimezone ?? "UTC")
          .startOf("day")
          .add(s.hour, "hours")
          .add(s.minute, "minutes");
        return {
          start: start.toISOString(),
          end: start.clone().add(duration, "minutes").toISOString(),
          ...(s.participants?.length
            ? { interviewerIds: s.participants.map((p) => p.value) }
            : {})
        };
      })
  );

const getRoosterLinkSessionEntityType = (
  meetingLink: MeetingLinkType
): "roosterlink" | "prospectlink" =>
  meetingLink.roosterLinksMeta?.profileType === "prospect"
    ? "prospectlink"
    : "roosterlink";

export const isStaleRequestSessionAttempt = (sessionStartedAt: number) => {
  const currentSessionStartedAt =
    useSchedulerStore.getState().sessionData?.requestSessionStartedAt;

  return (
    typeof currentSessionStartedAt === "number" &&
    currentSessionStartedAt !== sessionStartedAt
  );
};

const getExistingLinkApplicationId = (meetingLink: MeetingLinkType) =>
  meetingLink.scheduledData?.alreadyScheduled
    ? (meetingLink.scheduledData?.docDataRoosterLinks?.parent ?? undefined)
    : undefined;

export const loadAvailabilityForRoosterLink = async ({
  clientId,
  host,
  providers,
  sessionData,
  meetingLink,
  link,
  actions,
  ui,
  user,
  t,
  skipRecordingSession = false,
  mountTime
}: {
  clientId: string;
  host: HostType;
  link: SchedulerStoreType["link"];
  providers: Provider[];
  sessionData: SessionDataType;
  meetingLink: MeetingLinkType;
  actions: SchedulerStoreType["actions"];
  ui: SchedulerStoreType["ui"];
  user: SchedulerStoreType["user"];
  t: TFunction;
  skipRecordingSession?: boolean;
  mountTime: number;
}) => {
  if (
    meetingLink?.availability?.type === "range" &&
    sessionData?.selectedTimezone &&
    meetingLink.interviewers?.length &&
    link?.linkId
  ) {
    actions.setSessionData({
      ...sessionData,
      computedAvailability: []
    });

    const { queryWindowsResult: availability, unableToFetchParticipants } =
      await getAvailability({
        availabilityIdentifier: `${link.userId}/${link.linkId}`,
        type: "roosterLink",
        isProspectLink:
          meetingLink?.roosterLinksMeta?.profileType !== "standalone",
        clientId,
        providers,
        interviewers: meetingLink.interviewers,
        email: host.email,
        queryWindows:
          meetingLink?.availability?.range?.map((range) => ({
            timeMin: range.rangeStart,
            timeMax: range.rangeEnd
          })) ?? [],
        showTentativeAsBusy: meetingLink.showTentativeAsBusy ?? true,
        duration: meetingLink.availability.duration,
        timezone: sessionData.selectedTimezone,
        ...(host?.interviewerPreferences
          ? {
              overriddenInterviewerPreferences: {
                [host.email]: host.interviewerPreferences ?? {}
              }
            }
          : {})
      });
    if (
      unableToFetchParticipants.length &&
      ui?.currentStep !== "scheduling_cancelled"
    ) {
      await logErrorAndNotifyUserForRoosterLinks({
        errorCode: "ROOSTER_LINK_AVAILABILITY_FETCH_FAILED",
        context: {
          roosterLinkId: meetingLink.id ?? "",
          userUid: user?.id ?? ""
        }
      });
      handleRoosterLinkValidationErrorMessageState(
        true,
        false,
        { code: "ROOSTER_LINK_AVAILABILITY_FETCH_FAILED" },
        actions,
        ui,
        host,
        meetingLink?.roosterLinksMeta?.profileType === "prospect",
        t
      );
    }

    const computedAvailability = availability?.flatMap((a) => a.dates) ?? [];
    const computedAvailabilityFiltered =
      filterPreviouslySelectedSlotFromComputedAvailability(
        sessionData,
        computedAvailability,
        meetingLink.availability.duration
      );

    if (
      computedAvailability.length === 0 &&
      ui?.currentStep !== "scheduling_cancelled"
    ) {
      await logErrorAndNotifyUserForRoosterLinks({
        errorCode: "ROOSTER_LINK_AVAILABILITY_NO_DATES",
        context: {
          roosterLinkId: meetingLink.id ?? "",
          userUid: user?.id ?? ""
        }
      });
      handleRoosterLinkValidationErrorMessageState(
        true,
        false,
        { code: "ROOSTER_LINK_AVAILABILITY_NO_DATES" },
        actions,
        ui,
        host,
        meetingLink?.roosterLinksMeta?.profileType === "prospect",
        t
      );
    }

    if (availability) {
      if (isStaleRequestSessionAttempt(mountTime)) {
        return;
      }

      if (meetingLink.id && link?.userId && link?.linkId) {
        const { pageLoadedAt, pageLoadTimeMs, slotsLoadedAt } =
          getRequestSessionTiming(mountTime);
        const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(
          navigator.userAgent
        );
        const slots = buildRecordedSlots(
          computedAvailabilityFiltered,
          sessionData,
          meetingLink.availability.duration
        );
        const applicationId = getExistingLinkApplicationId(meetingLink);

        if (!skipRecordingSession) {
          void recordSession({
            entityId: meetingLink.id,
            entityType: getRoosterLinkSessionEntityType(meetingLink),
            client: clientId,
            roosterLinkUsername: link.userId,
            roosterLinkMeetingLink: link.linkId,
            pageLoadedAt,
            slotsLoadedAt,
            pageLoadTimeMs,
            isMobile,
            slots,
            candidateTimezone: sessionData.selectedTimezone,
            ...(applicationId ? { applicationId } : {}),
            actionType: sessionData?.isRescheduleFlow
              ? "reschedule"
              : "schedule"
          });
        }
      }

      actions.setSessionData({
        ...sessionData,
        prevSelectedSlot: sessionData?.prevSelectedSlot,
        selectedTimezone: sessionData.selectedTimezone,
        selectedSlot: sessionData?.selectedSlot ?? {
          date:
            moment(availability?.[0].dates?.[0]?.date).valueOf() ??
            moment().valueOf(),
          hour: null,
          minute: null
        },
        formData: sessionData?.formData,
        computedAvailability: computedAvailabilityFiltered
      });
    }
  }
};

export const loadAvailabilityForAutobook = async ({
  clientId,
  host,
  sessionData,
  meetingLink,
  link,
  actions,
  skipSettingSessionData = false,
  skipRecordingSession = false,
  ui,
  debug,
  t,
  mountTime
}: {
  clientId: string;
  host: HostType;
  providers: Provider[];
  link: SchedulerStoreType["link"];
  sessionData: SessionDataType;
  meetingLink: MeetingLinkType;
  actions: SchedulerStoreType["actions"];
  skipSettingSessionData?: boolean;
  skipRecordingSession?: boolean;
  ui: SchedulerStoreType["ui"];
  debug?: { enabled?: boolean; source?: string };
  t: TFunction;
  mountTime: number;
}) => {
  if (
    (meetingLink?.availability?.type === "range" ||
      meetingLink?.availability?.type === "individual_dates") &&
    meetingLink.availability.range &&
    sessionData?.selectedTimezone
  ) {
    const shouldCallAdvancedAvailabilityApi =
      meetingLink?.hasDynamicInterviewersFilter === true &&
      sessionData.isRescheduleFlow === false;
    const {
      queryWindowsResult: availability,
      unableToFetchParticipants,
      noAvailabilityReason
    } = shouldCallAdvancedAvailabilityApi
      ? await getAvailabilityAdvanced({
          availabilityIdentifier: link?.scheduleId ?? "",
          type: "rooster",
          jobReqId: meetingLink?.jobReqId,
          clientId,
          email: host.email,
          applicationId:
            meetingLink.scheduledData?.docDataRooster?.parent ?? "",
          interviewersFilter: meetingLink.interviewersFilter,
          onlyConsiderRoosterBlocks:
            meetingLink?.scheduledData?.docDataRooster
              ?.onlyConsiderRoosterBlocks ?? false,
          requiredInterviewers:
            meetingLink?.scheduledData?.docDataRooster
              ?.requiredInterviewersCount ?? 1,
          fillRate: {
            fillRateScheme: "perSegment"
          },
          allowOverbooking:
            meetingLink?.scheduledData?.docDataRooster?.allowOverbooking ??
            false,
          queryWindows:
            meetingLink?.availability?.range?.map((range) => ({
              timeMin: range.rangeStart,
              timeMax: range.rangeEnd
            })) ?? [],
          duration: meetingLink.availability.duration,
          showTentativeAsBusy: meetingLink.showTentativeAsBusy ?? true,
          incrementIntervalFreq: meetingLink.availability?.autobookIntervalFreq,
          timezone: sessionData.selectedTimezone,
          isRescheduleFlow: sessionData?.isRescheduleFlow ?? false,
          debug
        })
      : await getAvailabilityAdvanced({
          availabilityIdentifier: link?.scheduleId ?? "",
          jobReqId: meetingLink?.jobReqId,
          type: "rooster",
          clientId,
          email: host.email,
          applicationId:
            meetingLink.scheduledData?.docDataRooster?.parent ?? "",
          interviewersFilter: {
            requiredInterviewers:
              meetingLink.interviewers?.map((interviewer) => ({
                value: interviewer.value ?? interviewer.email,
                type: "email",
                id: "",
                displayName: ""
              })) ?? [],
            compositeFilter: {
              id: "root",
              operation: "OR",
              groups: [],
              isRoot: true
            }
          },
          onlyConsiderRoosterBlocks:
            meetingLink?.scheduledData?.docDataRooster
              ?.onlyConsiderRoosterBlocks ?? false,
          requiredInterviewers: meetingLink?.interviewers?.length ?? 0,
          fillRate: {
            maxRetryAttempts: 1,
            requiredFillRate: 0,
            fillRateScheme: "perSegment"
          },
          allowOverbooking:
            meetingLink?.scheduledData?.docDataRooster?.allowOverbooking ??
            true,
          queryWindows:
            meetingLink?.availability?.range?.map((range) => ({
              timeMin: range.rangeStart,
              timeMax: range.rangeEnd
            })) ?? [],
          duration: meetingLink.availability.duration,
          showTentativeAsBusy: meetingLink.showTentativeAsBusy ?? true,
          incrementIntervalFreq: meetingLink.availability?.autobookIntervalFreq,
          timezone: sessionData.selectedTimezone,
          isRescheduleFlow: sessionData?.isRescheduleFlow ?? false,
          debug
        });

    // An unreadable interviewer is returned as busy, so it only matters when
    // nobody else has a slot. Blocking on it otherwise hides every slot from
    // a tag pool because of one disabled mailbox (RSTR-24754). Count only
    // slots the candidate can pick: a reschedule drops the current booking.
    const hasAvailableSlot =
      filterPreviouslySelectedSlotFromComputedAvailability(
        sessionData,
        (availability ?? []).flatMap((window) => window.dates ?? []),
        meetingLink.availability.duration
      ).some((date) =>
        date.availability?.some((slot) => slot.available === true)
      );

    if (
      unableToFetchParticipants.length &&
      !hasAvailableSlot &&
      ui?.currentStep !== "scheduling_cancelled"
    ) {
      setUnableToLoadAvailabilityForParticipantError(actions, t);
    }

    if (availability) {
      const combinedAvailableDates = availability
        .flatMap((a) => a.dates)
        .reduce<(typeof availability)[0]["dates"]>((acc, curr) => {
          const existingDate = acc.find((item) => item.date === curr.date);
          if (existingDate) {
            existingDate.availability = [
              ...existingDate.availability,
              ...curr.availability
            ];
            return acc;
          }
          acc.push({
            ...curr,
            availability: curr.availability.filter((s) => s.available === true)
          });
          return acc;
        }, []);

      const computedAvailability = combinedAvailableDates ?? [];

      const sendNotificationEmailOnNoAvailability = !(
        meetingLink?.canReschedule === false && sessionData?.isRescheduleFlow
      );

      if (noAvailabilityReason) {
        actions.setNoAvailabilityLog(noAvailabilityReason);
      }

      if (
        computedAvailability.length === 0 &&
        ui?.currentStep !== "scheduled" &&
        ui?.currentStep !== "scheduling_cancelled"
      ) {
        if (
          sendNotificationEmailOnNoAvailability &&
          noAvailabilityReason &&
          link?.scheduleId
        ) {
          await notifySenderOnNoAvailability(
            noAvailabilityReason,
            link.scheduleId,
            sessionData
          );
        }
        setNoAvailabilityError(actions, t);
      }

      const computedAvailabilityFiltered =
        filterPreviouslySelectedSlotFromComputedAvailability(
          sessionData,
          computedAvailability,
          meetingLink.availability.duration
        );

      // Fire-and-forget session record — best-effort, never blocks the UI.
      // Fires for both initial visits and reschedule attempts; each creates
      // a new history row. The hook-level recording key prevents
      // double-insertion on frontend retries of the same attempt.
      // skipSettingSessionData=true means a pre-booking validation call — no session row.
      if (isStaleRequestSessionAttempt(mountTime)) {
        return null;
      }

      if (
        link?.scheduleId &&
        !skipSettingSessionData &&
        !skipRecordingSession
      ) {
        const { pageLoadedAt, pageLoadTimeMs, slotsLoadedAt } =
          getRequestSessionTiming(mountTime);
        const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(
          navigator.userAgent
        );
        const slots = buildRecordedSlots(
          computedAvailabilityFiltered,
          sessionData,
          meetingLink.availability?.duration ?? 0
        );
        void recordSession({
          entityId: link.scheduleId,
          entityType: "autobook",
          client: clientId,
          pageLoadedAt,
          slotsLoadedAt,
          pageLoadTimeMs,
          isMobile,
          slots,
          candidateTimezone: sessionData.selectedTimezone,
          applicationId:
            meetingLink.scheduledData?.docDataRooster?.parent ?? undefined,
          actionType: sessionData?.isRescheduleFlow ? "reschedule" : "schedule"
        });
      }

      const data = {
        ...sessionData,
        selectedTimezone: sessionData.selectedTimezone,
        selectedSlot: sessionData?.selectedSlot ?? {
          date:
            moment(computedAvailabilityFiltered?.[0]?.date).valueOf() ??
            moment().valueOf(),
          hour: null,
          minute: null
        },
        formData: sessionData?.formData,
        computedAvailability: computedAvailabilityFiltered
      };

      if (!skipSettingSessionData) {
        actions.setSessionData(data);
      }
      return data;
    }
  }
  return null;
};

/**
 * Records a candidate session row for the availability-request response page,
 * mirroring the autobook session capture in `loadAvailabilityForAutobook`.
 *
 * Best-effort and fire-and-forget — never blocks or throws. Fires once when the
 * availability page is ready for the candidate to act. `entityId` is the
 * availability/interview doc id (`meetingLink.id`), which the server-side
 * outcome stamp (on availability submit) keys on, so the INSERT here and the
 * later outcome UPDATE land on the same row.
 *
 * The availability page does not present pre-computed bookable slots; the
 * candidate marks their own availability within the offered window ranges, so
 * `slots` carries those offered windows (parity with autobook's proposed slots).
 */
export const recordAvailabilitySession = ({
  clientId,
  meetingLink,
  sessionData,
  mountTime
}: {
  clientId: string;
  meetingLink: MeetingLinkType;
  sessionData: SessionDataType;
  mountTime: number;
}) => {
  const entityId = meetingLink?.id;
  if (!entityId || !clientId || !sessionData?.selectedTimezone) {
    return;
  }
  const { pageLoadedAt, pageLoadTimeMs, slotsLoadedAt } =
    getRequestSessionTiming(sessionData.requestSessionStartedAt ?? mountTime);
  const isMobile = /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const slots =
    meetingLink?.availability?.range?.map((range) => ({
      start: range.rangeStart,
      end: range.rangeEnd
    })) ?? [];

  void recordSession({
    entityId,
    entityType: "availability",
    client: clientId,
    pageLoadedAt,
    slotsLoadedAt,
    pageLoadTimeMs,
    isMobile,
    slots,
    candidateTimezone: sessionData.selectedTimezone,
    applicationId:
      meetingLink.scheduledData?.docDataRooster?.parent ?? undefined,
    actionType: sessionData?.isRescheduleFlow ? "reschedule" : "schedule"
  });
};

export const getAvailability = async ({
  clientId,
  email,
  queryWindows,
  duration,
  interviewers,
  showTentativeAsBusy,
  providers,
  incrementIntervalFreq,
  availabilityIdentifier,
  type,
  overriddenInterviewerPreferences,
  timezone,
  jobReqId,
  isProspectLink,
  debug
}: {
  availabilityIdentifier: string;
  type: string;
  clientId: string;
  email: string;
  queryWindows: { timeMin: string; timeMax: string }[];
  duration: number;
  showTentativeAsBusy: boolean;
  interviewers: InterviewerType[];
  providers: Provider[];
  timezone: string;
  incrementIntervalFreq?: number;
  overriddenInterviewerPreferences?: Record<
    string,
    IOverriddenInterviewerPreferences
  >;
  jobReqId?: string;
  isProspectLink?: boolean;
  debug?: { enabled?: boolean; source?: string };
}) => {
  const availabilityData = await getAvailabilityData({
    availabilityIdentifier,
    type,
    client: clientId,
    requesterEmail: email,
    jobReqId,
    availabilityInterval: {
      intervalStartsFrom: "startOfDay",
      value: incrementIntervalFreq ?? 30
    },
    eventDuration: duration,
    overriddenInterviewerPreferences,
    queryWindows,
    queryResources: [
      {
        participants: interviewers?.map((i) => ({
          type: "interviewer",
          value: i.value ?? i.email,
          provider: providers.find((p) => p.email === i.email)?.provider ?? ""
        })),
        algorithm: {
          type: "collective"
        }
      }
    ],
    responseFormat: {
      showTentativeAsBusy: showTentativeAsBusy,
      tzid: timezone,
      hideUnavailableSlot: true,
      removeMultiDaySlot: false,
      type: "slot",
      batchAdjacentQueryWindows: true
    },
    fetchMethod: ["freebusy.api", "events.api"],
    makeParticipantAsBusyWhenUnableToFetchEvents: true,
    skipInterviewerPreferencesSync: false,
    isProspectLink,
    ...(debug?.enabled && {
      debug: {
        enabled: true,
        sessionUrl: logRocketSessionUrl ?? "",
        source: debug?.source ?? ""
      }
    })
  });
  return {
    queryWindowsResult: availabilityData?.queryWindowsResult,
    unableToFetchParticipants: availabilityData?.unableToFetchParticipants ?? []
  };
};

export const getAvailabilityAdvanced = async ({
  clientId,
  email,
  applicationId,
  interviewersFilter,
  onlyConsiderRoosterBlocks,
  requiredInterviewers,
  fillRate,
  queryWindows,
  duration,
  jobReqId,
  allowOverbooking,
  showTentativeAsBusy,
  incrementIntervalFreq,
  availabilityIdentifier,
  type,
  timezone,
  debug,
  isRescheduleFlow = false
}: {
  availabilityIdentifier: string;
  applicationId: string;
  type: string;
  jobReqId?: string;
  clientId: string;
  email: string;
  queryWindows: { timeMin: string; timeMax: string }[];
  duration: number;
  showTentativeAsBusy: boolean;
  allowOverbooking: boolean;
  requiredInterviewers: number;
  interviewersFilter?: InterviewersFilter | null;
  timezone: string;
  incrementIntervalFreq?: number;
  fillRate: {
    maxRetryAttempts?: number;
    requiredFillRate?: number;
    fillRateScheme: fillRateSchemeType;
  };
  onlyConsiderRoosterBlocks: boolean;
  debug?: { enabled?: boolean; source?: string };
  isRescheduleFlow?: boolean;
}) => {
  const availabilityData = await getAvailabilityAdvancedData({
    availabilityIdentifier,
    applicationId,
    jobReqId,
    type,
    client: clientId,
    requesterEmail: email,
    availabilityInterval: {
      intervalStartsFrom: "startOfDay",
      value: incrementIntervalFreq ?? 30
    },
    eventDuration: duration,
    queryWindows,
    allowOverbooking,
    filter: interviewersFilter,
    fillRate,
    onlyConsiderRoosterBlocks,
    responseFormat: {
      showTentativeAsBusy: showTentativeAsBusy,
      tzid: timezone,
      hideUnavailableSlot: true,
      removeMultiDaySlot: false,
      type: "slot",
      batchAdjacentQueryWindows: true
    },
    fetchMethod: ["freebusy.api", "events.api"],
    makeParticipantAsBusyWhenUnableToFetchEvents: true,
    skipInterviewerPreferencesSync: true,
    requiredInterviewers,
    ...(debug?.enabled && {
      debug: {
        enabled: true,
        sessionUrl: logRocketSessionUrl ?? "",
        source: debug?.source ?? ""
      }
    }),
    autoIncrementCapacityForReschedule: isRescheduleFlow
  });
  return {
    queryWindowsResult: availabilityData?.queryWindowsResult,
    unableToFetchParticipants:
      availabilityData?.unableToFetchParticipants ?? [],
    noAvailabilityReason: availabilityData?.noAvailabilityReason
  };
};

export const submitAvailability = async ({
  availabilityId,
  clientTimeZone,
  candidateComments,
  submittedAvailability,
  client
}: {
  client: string;
  availabilityId: string;
  clientTimeZone: string;
  candidateComments: string;
  submittedAvailability: ISubmitAvailabilityPayload["availability"];
}) => {
  const result = await submitAvailabilityData(client, {
    id: availabilityId,
    clientTimeZone: clientTimeZone,
    sessionURL: logRocketSessionUrl ?? "",
    candidateComments: candidateComments,
    availability: submittedAvailability
  });
  return result;
};
